const CORE_PREFIX = '/api/v1/core';

export class CoreApiError extends Error {
  constructor(message, { status = 0, code = null, detail = null, payload = null } = {}) {
    super(message);
    this.name = 'CoreApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.payload = payload;
  }

  get isAuthError() {
    return this.status === 401;
  }
}

function baseUrl() {
  const configured =
    import.meta.env.VITE_CORE_API_URL ||
    import.meta.env.VITE_BACKEND_URL ||
    '';
  return String(configured).replace(/\/+$/, '');
}

function url(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl()}${CORE_PREFIX}${normalized}`;
}

async function request(path, { method = 'GET', token, body, idempotencyKey, signal } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response;
  try {
    response = await fetch(url(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'omit',
      cache: 'no-store',
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new CoreApiError('Core API is unreachable', { detail: error?.message });
  }

  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { detail: text };
    }
  }

  if (!response.ok) {
    throw new CoreApiError(payload?.detail || payload?.message || `Core API ${response.status}`, {
      status: response.status,
      code: payload?.code || null,
      detail: payload?.detail || null,
      payload,
    });
  }

  return payload;
}

export function newIdempotencyKey(scope = 'intent') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${scope}-${crypto.randomUUID()}`;
  }
  return `${scope}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function createGuest(displayName = null) {
  return request('/auth/guest', {
    method: 'POST',
    body: displayName ? { display_name: displayName } : {},
  });
}

export function getHealth(signal) {
  return request('/health', { signal });
}

export function getMe(token, signal) {
  return request('/me', { token, signal });
}

export function getState(token, signal) {
  return request('/state', { token, signal });
}

export function getContentPack(signal) {
  return request('/content/pack', { signal });
}

export function getQuote(token, assetId, side, quantity = 1, signal) {
  const params = new URLSearchParams({
    asset_id: assetId,
    side,
    quantity: String(quantity),
  });
  return request(`/market/quote?${params.toString()}`, { token, signal });
}

export function listFragments(token) {
  return request('/fragments', { token });
}

export function executeTrade(token, { assetId, side, quantity }, idempotencyKey) {
  return request('/trade', {
    method: 'POST',
    token,
    idempotencyKey,
    body: { asset_id: assetId, side, quantity },
  });
}

export function getTicket(token, signal) {
  return request('/ticket', { token, signal });
}

export function buyTicket(token, toDistrictId, idempotencyKey) {
  return request('/ticket/buy', {
    method: 'POST',
    token,
    idempotencyKey,
    body: { to_district_id: toDistrictId },
  });
}

export function executeTravel(token, toDistrictId, idempotencyKey) {
  return request('/travel', {
    method: 'POST',
    token,
    idempotencyKey,
    body: { to_district_id: toDistrictId },
  });
}

export function getRunResult(token, runId, signal) {
  return request(`/runs/${encodeURIComponent(runId)}/result`, { token, signal });
}

export function listRuns(token, limit = 20, signal) {
  return request(`/runs?limit=${limit}`, { token, signal });
}

export function startRun(token) {
  return request('/runs', { method: 'POST', token });
}

export function advanceRun(token, runId) {
  return request(`/runs/${encodeURIComponent(runId)}/advance`, { method: 'POST', token });
}

export function waitTurn(token, runId) {
  return request(`/runs/${encodeURIComponent(runId)}/wait`, { method: 'POST', token });
}

export function listLoans(token, signal) {
  return request('/loans', { token, signal });
}

export function openLoan(token, amount, idempotencyKey) {
  return request('/loans', {
    method: 'POST',
    token,
    idempotencyKey,
    body: { principal: { amount: String(amount), currency: 'DCW_USD' } },
  });
}

export function repayLoan(token, loanId, amount, idempotencyKey) {
  return request(`/loans/${encodeURIComponent(loanId)}/repay`, {
    method: 'POST',
    token,
    idempotencyKey,
    body: { amount: { amount: String(amount), currency: 'DCW_USD' } },
  });
}

export function bribeHeat(token, idempotencyKey) {
  return request('/heat/bribe', { method: 'POST', token, idempotencyKey });
}

export function expandCoat(token, idempotencyKey) {
  return request('/coat/expand', { method: 'POST', token, idempotencyKey });
}

export function listHronir(token, signal) {
  return request('/hronir', { token, signal });
}

export function acquireHronir(token, idempotencyKey) {
  return request('/hronir/acquire', { method: 'POST', token, idempotencyKey });
}

export function hronirVerb(token, hronirId, verb, idempotencyKey) {
  return request(`/hronir/${encodeURIComponent(hronirId)}/verb`, {
    method: 'POST',
    token,
    idempotencyKey,
    body: { verb },
  });
}

export function startStory(token) {
  return request('/story/start', { method: 'POST', token });
}

export function getStory(token, signal) {
  return request('/story', { token, signal });
}

export function chooseStory(token, choiceIndex) {
  return request('/story/choose', {
    method: 'POST',
    token,
    body: { choice_index: choiceIndex },
  });
}

export function listMarketEvents(token, signal) {
  return request('/market/events', { token, signal });
}

export function listDrops(token, signal) {
  return request('/drops', { token, signal });
}

export function listClaims(token, { status, signal } = {}) {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  return request(`/claims${query}`, { token, signal });
}

export function claimPendingClaim(token, claimId, idempotencyKey) {
  return request(`/claims/${encodeURIComponent(claimId)}/claim`, {
    method: 'POST',
    token,
    idempotencyKey,
  });
}

export function listEvents(token, limit = 100, signal) {
  return request(`/events?limit=${limit}`, { token, signal });
}

export function getChatPresence(token, signal) {
  return request('/chat/presence', { token, signal });
}

export function listChatThreads(token, signal) {
  return request('/chat/threads', { token, signal });
}

export function getChatThread(token, characterId, signal) {
  return request(`/chat/threads/${encodeURIComponent(characterId)}/messages`, {
    token,
    signal,
  });
}

/**
 * Store a player message and let the server answer it (V2-L9e).
 *
 * The body is the only client input: the adapter owns the persona, lane,
 * caps and token. No prompt, model or clock is ever sent from here.
 */
export function replyChatThread(token, characterId, body, signal) {
  return request(`/chat/threads/${encodeURIComponent(characterId)}/reply`, {
    method: 'POST',
    token,
    body: { body },
    signal,
  });
}

export function getDayClose(token, seasonId, dayIndex, signal) {
  const params = new URLSearchParams({
    season_id: seasonId,
    day_index: String(dayIndex),
  });
  return request(`/day/close?${params.toString()}`, { token, signal });
}

export function getReplay(token, signal) {
  return request('/replay', { token, signal });
}
